import React, {useRef, useState} from "react";
import {C} from "../styles";
import {apiAdminLandingAiReview} from "../api/landing";
import SECTION_FIELDS from "../landing/sectionFields.json";
import BLOCK_FIELDS from "../landing/blockFields.json";

const SEVERITY_LABELS = {warning: "À corriger", info: "À savoir"};

const smallButton = {border: `1px solid ${C.border}`, background: "#fff", borderRadius: 6, padding: "4px 9px", cursor: "pointer", fontSize: 13};

// Nom d'une rubrique et type d'un bloc, tels que l'admin les voit dans la liste et dans les éditeurs.
function locate(page, finding) {
    const section = page.sections.find(s => s.id === finding.section_id);
    if (!section) return "";
    const name = section.type === "custom"
        ? ((section.settings && section.settings.name) || SECTION_FIELDS.custom.label)
        : (SECTION_FIELDS[section.type] ? SECTION_FIELDS[section.type].label : section.type);
    if (!finding.block_id) return name;
    const block = (section.blocks || []).find(b => b.id === finding.block_id);
    const blockLabel = block && BLOCK_FIELDS.blocks[block.type] ? BLOCK_FIELDS.blocks[block.type].label : "";
    return blockLabel ? `${name} › ${blockLabel}` : name;
}

function FindingGroup({title, findings, page, onIgnore, onOpenSection}) {
    if (findings.length === 0) return null;
    return (
        <section aria-label={title} style={{marginTop: 12}}>
            <h4 style={{margin: "0 0 6px"}}>{title}</h4>
            {findings.map(f => (
                <div key={f.id} style={{border: `1px solid ${C.border}`, borderRadius: 8, padding: "8px 10px", marginBottom: 6, background: "#fff"}}>
                    <div style={{display: "flex", gap: 8, alignItems: "baseline", flexWrap: "wrap"}}>
                        <strong style={{fontSize: 12}}>{SEVERITY_LABELS[f.severity] || SEVERITY_LABELS.info}</strong>
                        <span style={{fontSize: 12, color: C.textLight}}>{locate(page, f)}</span>
                    </div>
                    <div style={{marginTop: 4}}>{f.message}</div>
                    {f.suggestion && <div style={{marginTop: 4, fontSize: 13}}>Suggestion : {f.suggestion}</div>}
                    <div style={{display: "flex", gap: 6, marginTop: 6}}>
                        <button type="button" style={smallButton} onClick={() => onOpenSection(f.section_id, f.block_id || null)}>Aller à cette rubrique</button>
                        <button type="button" style={smallButton} onClick={() => onIgnore(f.id)}>Ignorer</button>
                    </div>
                </div>
            ))}
        </section>
    );
}

// Relecture de la page : contrôles automatiques (toujours) et relecture IA (si configurée). Ne fait que LIRE et
// afficher : ni enregistrement, ni publication, ni modification de la page. « Ignorer » ne vit qu'ici, dans l'écran.
export default function LandingReviewPanel({page, aiAvailable, beforeReview, onOpenSection}) {
    const busy = useRef(false);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState("");
    const [result, setResult] = useState(null); // {checks, ai, ai_error}
    const [ignored, setIgnored] = useState([]);

    async function run() {
        if (busy.current) return;
        busy.current = true; // verrou synchrone : un double clic n'envoie qu'une requête
        setLoading(true);
        setError("");
        try {
            // Relire l'état réel : le dernier changement en attente est d'abord enregistré par l'écran parent.
            if (!(await beforeReview())) return;
            const res = await apiAdminLandingAiReview(page);
            if (!res || res.error || !Array.isArray(res.checks)) {
                setError((res && (res.message || res.error)) || "La relecture n'a pas pu être faite.");
                return;
            }
            setResult(res);
            setIgnored([]);
        } finally {
            busy.current = false;
            setLoading(false);
        }
    }

    const visible = (list) => (Array.isArray(list) ? list : []).filter(f => !ignored.includes(f.id));
    const checks = result ? visible(result.checks) : [];
    const ai = result ? visible(result.ai) : [];
    const total = checks.length + ai.length;
    const ignore = (id) => setIgnored(list => [...list, id]);

    return (
        <div style={{marginTop: 16, borderTop: `1px solid ${C.border}`, paddingTop: 12}}>
            <div style={{display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap"}}>
                <button type="button" style={smallButton} disabled={loading} onClick={run}>
                    {loading ? "Relecture en cours…" : "🔍 Relire la page"}
                </button>
                {result && total > 0 && <span style={{fontSize: 13}}>{total === 1 ? "1 constat" : `${total} constats`}</span>}
            </div>
            {!aiAvailable && (
                <p style={{fontSize: 13, margin: "6px 0 0"}}>L'aide IA n'est pas configurée : seuls les contrôles automatiques sont faits.</p>
            )}
            {error && <p role="alert" style={{color: C.danger, margin: "8px 0 0"}}>{error}</p>}
            {result && aiAvailable && result.ai === null && (
                <p style={{fontSize: 13, margin: "8px 0 0"}}>
                    La relecture IA est indisponible ({result.ai_error}). Les contrôles automatiques restent valables.
                </p>
            )}
            {result && total === 0 && <p style={{margin: "8px 0 0"}}>Aucun point à signaler</p>}
            {result && (
                <>
                    <FindingGroup title="Contrôles automatiques" findings={checks} page={page} onIgnore={ignore} onOpenSection={onOpenSection}/>
                    <FindingGroup title="Relecture IA" findings={ai} page={page} onIgnore={ignore} onOpenSection={onOpenSection}/>
                </>
            )}
        </div>
    );
}
